import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User, Scenario } from '../types/auth';

import quickstartService from '../services/quickstart-service';
import scenarioService from '../services/scenario-service';
import AdvancedRetirementPlanner from './AdvancedRetirementPlanner';
import USRetirementPlanner from './USRetirementPlanner';
import StructuredData from './StructuredData';

import GuidanceModal from './GuidanceModal';
import { TylerState } from '../utils/TylerState';
import { debugLog } from '../utils/debugLogger';
import { useCountry } from '../contexts/CountryContext';

interface RRSPCalculatorProps {
  user?: any;
  guestScenario?: any;
  onSaveScenario?: (data: any) => void;
}

interface SavedRRSPScenario {
  id: string;
  name: string;
  data: any;
  createdAt: string;
}

const RRSPCalculator: React.FC<RRSPCalculatorProps> = ({ user, guestScenario, onSaveScenario }) => {
  const { isUSA } = useCountry();

  const [currentScenario, setCurrentScenario] = useState<Scenario | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isTestMode, setIsTestMode] = useState(false);
  const [analysisResults, setAnalysisResults] = useState<any>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [triggerAnalysis, setTriggerAnalysis] = useState(false);
  const [savedScenarios, setSavedScenarios] = useState<SavedRRSPScenario[]>([]);
  const [saveName, setSaveName] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);

  // Guidance modal state
  const [showGuidanceModal, setShowGuidanceModal] = useState(false);
  const [guidanceTitle, setGuidanceTitle] = useState('');
  const [guidanceContent, setGuidanceContent] = useState('');

  // Storage key for saved scenarios (used for migration only)
  const storageKey = TylerState.user ? `rrsp_scenarios_${TylerState.user.email}` : '';

  // Set page title and meta description for SEO
  useEffect(() => {
    if (isUSA) {
      document.title = 'Advanced Retirement Planner USA | Larry\'s Help';
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Comprehensive retirement planning with Monte Carlo simulation. Optimize 401(k), Roth IRA, Social Security, and tax strategies. Model 10,000+ scenarios for confident retirement decisions.');
      }
    } else {
      document.title = 'Advanced Retirement Planner Canada | Larry\'s Help';
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Comprehensive retirement planning with Monte Carlo simulation. Optimize RRSP, TFSA, CPP, OAS, and tax strategies. Model 10,000+ scenarios for confident retirement decisions.');
      }
    }
  }, [isUSA]);

  // Load saved scenarios from database
  useEffect(() => {
    const loadScenarios = async () => {
      if (!TylerState.user?.email) {
        setSavedScenarios([]);
        return;
      }

      try {
        // Fetch from database
        const dbScenarios = await scenarioService.getUserScenarios();
        const retirementScenarios = dbScenarios.filter(s => s.scenario_type === 'retirement');

        // Convert to SavedRRSPScenario format
        const converted: SavedRRSPScenario[] = retirementScenarios.map(s => {
          const scenarioData = typeof s.scenario_data === 'string'
            ? JSON.parse(s.scenario_data)
            : s.scenario_data;

          return {
            id: s.id.toString(),
            name: s.name,
            data: scenarioData,
            createdAt: s.created_at
          };
        });

        setSavedScenarios(converted);

        // Migration: Check localStorage for old scenarios
        const localRaw = localStorage.getItem(storageKey);
        if (localRaw) {
          try {
            const localScenarios: SavedRRSPScenario[] = JSON.parse(localRaw);

            // Save each local scenario to database if it doesn't already exist
            for (const localScenario of localScenarios) {
              const exists = converted.find(s => s.name === localScenario.name);
              if (!exists) {
                await scenarioService.createScenario({
                  name: localScenario.name,
                  description: '',
                  scenario_type: 'retirement',
                  scenario_data: localScenario.data,
                  is_public: false
                });
              }
            }

            // Clear localStorage after successful migration
            localStorage.removeItem(storageKey);

            // Reload scenarios after migration
            const updatedScenarios = await scenarioService.getUserScenarios();
            const updatedRetirement = updatedScenarios.filter(s => s.scenario_type === 'retirement');
            const updatedConverted: SavedRRSPScenario[] = updatedRetirement.map(s => {
              const scenarioData = typeof s.scenario_data === 'string'
                ? JSON.parse(s.scenario_data)
                : s.scenario_data;

              return {
                id: s.id.toString(),
                name: s.name,
                data: scenarioData,
                createdAt: s.created_at
              };
            });
            setSavedScenarios(updatedConverted);
          } catch (migrationError) {
            console.error('Error migrating localStorage scenarios:', migrationError);
          }
        }
      } catch (error) {
        console.error('Error loading saved scenarios from database:', error);
        setSavedScenarios([]);
      }
    };

    loadScenarios();
  }, [TylerState.user?.email, storageKey]);

  // Route US users to the US-specific planner (after all hooks)
  if (isUSA) return <USRetirementPlanner />;

  const getScenarioCap = (tier: User['tier']) => {
    if (tier === 'supporter') return 1000;
    return 1; // free default
  };

  const handleNewScenario = () => {
    setCurrentScenario(null);
    setIsTestMode(false);
    setAnalysisResults(null);
    setErrors([]);
  };

  const handleQuickStart = () => {
    setCurrentScenario(null);
    setIsTestMode(false);
    setAnalysisResults(null);
    setErrors([]);
  };

  const handleRunSimulation = async () => {
    // Trigger the disclaimer modal in AdvancedRetirementPlanner
    setTriggerAnalysis(true);
  };

  const handleAnalysisTriggered = () => {
    // Reset the trigger after it's been handled
    setTriggerAnalysis(false);
    // The actual simulation will be handled by AdvancedRetirementPlanner after disclaimer acceptance
  };

  const handleQuickStartTest = async () => {
    try {
      setIsLoading(true);
      const testScenario = await quickstartService.getTestScenario();
      
      // Convert the test scenario to the format expected by the planner
      const convertedScenario: Scenario = {
        id: 0,
        name: testScenario.name,
        description: testScenario.description,
        scenario_type: 'retirement',
        scenario_data: testScenario.scenario_data,
        is_public: testScenario.is_public,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      setCurrentScenario(convertedScenario);
      setIsTestMode(true);
    } catch (error) {
      console.error('Failed to load test scenario:', error);
      // Fall back to regular quickstart
      handleQuickStart();
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveScenario = async (scenarioData: any) => {
    if (!TylerState.user) {
      window.dispatchEvent(new Event('open-login-modal'));
      return;
    }

    if (!scenarioData) {
      setGuidanceTitle('No Data to Save');
      setGuidanceContent('Please run an analysis first so we can save your retirement scenario results.');
      setShowGuidanceModal(true);
      return;
    }

    const name = (saveName || '').trim();
    const finalName = name || `RRSP Scenario ${new Date().toLocaleString()}`;

    // Check if we're overwriting an existing scenario
    const existingScenario = savedScenarios.find(s => s.name.toLowerCase() === finalName.toLowerCase());

    // Only check tier limits if we're creating a new scenario (not overwriting)
    if (!existingScenario) {
      const cap = getScenarioCap(user.tier || 'free');
      if (savedScenarios.length >= cap) {
        setGuidanceTitle('Save Limit Reached');
        setGuidanceContent(`Free accounts can save ${cap} scenario${cap === 1 ? '' : 's'}.<br><br>Upgrade to Supporter ($10/year) for unlimited saves and advanced features.`);
        setShowGuidanceModal(true);
        return;
      }
    }

    try {
      if (existingScenario) {
        // Update existing scenario in database
        await scenarioService.updateScenario(parseInt(existingScenario.id), {
          name: finalName,
          description: '',
          scenario_type: 'retirement',
          scenario_data: scenarioData,
          is_public: false
        });

        debugLog('💾 SAVING RRSP SCENARIO (UPDATE)', { name: finalName });

        // Update local state
        const updatedScenarios = savedScenarios.map(s =>
          s.id === existingScenario.id
            ? { ...s, data: scenarioData }
            : s
        );
        setSavedScenarios(updatedScenarios);
      } else {
        // Create new scenario in database
        const created = await scenarioService.createScenario({
          name: finalName,
          description: '',
          scenario_type: 'retirement',
          scenario_data: scenarioData,
          is_public: false
        });

        debugLog('✅ SAVING RRSP SCENARIO (NEW)', { name: finalName });

        const newScenario: SavedRRSPScenario = {
          id: created.id.toString(),
          name: created.name,
          data: scenarioData,
          createdAt: created.created_at
        };

        setSavedScenarios([newScenario, ...savedScenarios]);
      }

      setSaveName('');
      setShowSaveModal(false);
      setGuidanceTitle(existingScenario ? 'Updated' : 'Saved');
      setGuidanceContent(`Your retirement scenario was ${existingScenario ? 'updated' : 'saved'}. You can load it anytime to edit or compare.`);
      setShowGuidanceModal(true);
    } catch (error) {
      console.error('Error saving scenario to database:', error);
      setGuidanceTitle('Error');
      setGuidanceContent('Failed to save scenario. Please try again.');
      setShowGuidanceModal(true);
    }
  };

  const handleLoadScenario = (id: string) => {
    const scenario = savedScenarios.find(s => s.id === id);
    if (!scenario) return;

    debugLog('📂 LOADING RRSP DATA', { name: scenario.name });

    setCurrentScenario({
      id: parseInt(scenario.id),
      name: scenario.name,
      description: scenario.name,
      scenario_type: 'retirement',
      scenario_data: scenario.data,
      is_public: false,
      created_at: scenario.createdAt,
      updated_at: scenario.createdAt
    });
    setSaveName(scenario.name); // Populate the scenario name input for updates
    setGuidanceTitle('Loaded');
    setGuidanceContent(`Loaded scenario: ${scenario.name}. The scenario name field has been updated so you can edit and save changes.`);
    setShowGuidanceModal(true);
  };

  const handleDeleteScenario = async (id: string) => {
    try {
      // Delete from database
      await scenarioService.deleteScenario(parseInt(id));

      // Update local state
      const updatedScenarios = savedScenarios.filter(s => s.id !== id);
      setSavedScenarios(updatedScenarios);

      setGuidanceTitle('Deleted');
      setGuidanceContent('Retirement scenario deleted successfully.');
      setShowGuidanceModal(true);
    } catch (error) {
      console.error('Error deleting scenario from database:', error);
      setGuidanceTitle('Error');
      setGuidanceContent('Failed to delete scenario. Please try again.');
      setShowGuidanceModal(true);
    }
  };

  return (
    <div className="rrsp-calculator">
      {/* SEO Structured Data */}
      <StructuredData
        type="faq"
        data={{
          faqs: isUSA ? [
            {
              question: "Should I contribute to a 401(k) or Roth IRA?",
              answer: "It depends on your current tax bracket and retirement plans. 401(k) contributions provide immediate tax deductions and are better for high earners who expect lower income in retirement. Roth IRAs offer tax-free growth and withdrawals with more flexibility. Our calculator simulates both scenarios to show which maximizes your retirement savings."
            },
            {
              question: "How does the retirement calculator work?",
              answer: "Our advanced retirement calculator uses Monte Carlo simulation to model thousands of possible market outcomes. It accounts for your contributions, tax rates, investment returns, inflation, Social Security benefits, and spending needs to show your probability of retirement success and optimal contribution strategy."
            },
            {
              question: "What is Monte Carlo simulation in retirement planning?",
              answer: "Monte Carlo simulation runs thousands of scenarios with different market returns to estimate the probability of your retirement plan succeeding. Instead of assuming average returns, it models realistic market volatility to give you a confidence level (e.g., 85% chance of success) for your retirement strategy."
            },
            {
              question: "When should I prioritize 401(k) over Roth IRA contributions?",
              answer: "Prioritize 401(k) when your current tax bracket is high (over 32%) and you expect to be in a lower bracket in retirement. The immediate tax deduction and tax-deferred growth make 401(k)s powerful for high earners. Use our calculator to compare the after-tax value of both strategies for your situation."
            },
            {
              question: "How much do I need to save for retirement in the US?",
              answer: "Most Americans need 70-80% of their pre-retirement income. Our calculator helps determine your exact savings target based on your desired retirement lifestyle, expected Social Security benefits, investment strategy, and retirement timeline. It accounts for inflation and market volatility to ensure you don't outlive your money."
            },
            {
              question: "What's a safe withdrawal rate in retirement?",
              answer: "The traditional 4% rule is a starting point, but our calculator uses more sophisticated modeling to determine your personalized safe withdrawal rate. It considers your actual portfolio mix, retirement timeline, Social Security income, and desired success probability to recommend sustainable spending levels."
            },
            {
              question: "What are the 401(k) contribution limits?",
              answer: "The 401(k) contribution limit is $23,500 for 2025, with an additional $7,500 catch-up contribution for those 50 and older. Employer matching does not count toward this limit. Our calculator tracks your contribution room and helps you maximize tax-deferred growth within these limits."
            },
            {
              question: "How does Social Security affect my retirement savings needs?",
              answer: "The average Social Security benefit is about $1,900/month (2025). This government benefit reduces how much you need to save personally. Our calculator includes realistic Social Security projections based on your earnings history to show your true savings gap and optimal contribution strategy."
            },
            {
              question: "Can I withdraw from my 401(k) before retirement?",
              answer: "Yes, but you'll pay a 10% early withdrawal penalty plus income tax on the withdrawal before age 59½. Early 401(k) withdrawals should be avoided except for hardship distributions or specific exceptions. Our calculator shows how early withdrawals impact your retirement readiness."
            },
            {
              question: "What's better for retirement: stocks or bonds in my 401(k)?",
              answer: "A balanced mix is typically best. Younger savers can handle more stocks (70-90%) for growth, while those near retirement should hold more bonds (40-60%) for stability. Our calculator lets you test different asset allocations and shows how each affects your retirement success probability."
            }
          ] : [
            {
              question: "Should I contribute to RRSP or TFSA in Canada?",
              answer: "It depends on your current tax bracket and retirement plans. RRSPs provide immediate tax deductions and are better for high earners who expect lower income in retirement. TFSAs offer tax-free growth and withdrawals with more flexibility. Our calculator simulates both scenarios to show which maximizes your retirement savings."
            },
            {
              question: "How does the RRSP calculator work?",
              answer: "Our advanced retirement calculator uses Monte Carlo simulation to model thousands of possible market outcomes. It accounts for your contributions, tax rates, investment returns, inflation, CPP/OAS benefits, and spending needs to show your probability of retirement success and optimal contribution strategy."
            },
            {
              question: "What is Monte Carlo simulation in retirement planning?",
              answer: "Monte Carlo simulation runs thousands of scenarios with different market returns to estimate the probability of your retirement plan succeeding. Instead of assuming average returns, it models realistic market volatility to give you a confidence level (e.g., 85% chance of success) for your retirement strategy."
            },
            {
              question: "When should I prioritize RRSP over TFSA contributions?",
              answer: "Prioritize RRSPs when your current tax bracket is high (over 40%) and you expect to be in a lower bracket in retirement. The immediate tax deduction and tax-deferred growth make RRSPs powerful for high earners. Use our calculator to compare the after-tax value of both strategies for your situation."
            },
            {
              question: "How much do I need to save for retirement in Canada?",
              answer: "Most Canadians need 70-80% of their pre-retirement income. Our calculator helps determine your exact savings target based on your desired retirement lifestyle, expected CPP/OAS benefits, investment strategy, and retirement timeline. It accounts for inflation and market volatility to ensure you don't outlive your money."
            },
            {
              question: "What's a safe withdrawal rate in retirement?",
              answer: "The traditional 4% rule is a starting point, but our calculator uses more sophisticated modeling to determine your personalized safe withdrawal rate. It considers your actual portfolio mix, retirement timeline, CPP/OAS income, and desired success probability to recommend sustainable spending levels."
            },
            {
              question: "What is the RRSP contribution limit in Canada?",
              answer: "Your RRSP contribution limit is 18% of your previous year's earned income, up to the annual maximum ($31,560 for 2024). Unused contribution room carries forward indefinitely. Our calculator tracks your contribution room and helps you maximize tax-deferred growth within these limits."
            },
            {
              question: "How does CPP and OAS affect my retirement savings needs?",
              answer: "CPP provides an average of $15,000/year and OAS about $8,000/year (2024 rates). These government benefits reduce how much you need to save personally. Our calculator includes realistic CPP/OAS projections based on your earnings history to show your true savings gap and optimal contribution strategy."
            },
            {
              question: "Can I withdraw from my RRSP before retirement?",
              answer: "Yes, but you'll pay withholding tax (10-30%) plus income tax on the withdrawal. Early RRSP withdrawals should be avoided except for emergencies or the Home Buyers' Plan (HBP) and Lifelong Learning Plan (LLP). Our calculator shows how early withdrawals impact your retirement readiness."
            },
            {
              question: "What's better for retirement: stocks or bonds in my RRSP?",
              answer: "A balanced mix is typically best. Younger savers can handle more stocks (70-90%) for growth, while those near retirement should hold more bonds (40-60%) for stability. Our calculator lets you test different asset allocations and shows how each affects your retirement success probability."
            }
          ]
        }}
      />
      <StructuredData
        type="breadcrumb"
        data={{
          items: [
            { name: "Home", url: "https://larryshelp.org" },
            { name: "Advanced Retirement Planner", url: "https://larryshelp.org/rrsp" }
          ]
        }}
      />

      <div className="car-comparison-calculator">
      <div className="car-planner-header">
        <h1>Advanced Retirement Planner</h1>
        <p>Comprehensive retirement planning with scenario simulation</p>
        <div style={{ marginTop: 8 }}>
          <Link to="/retirement-planner-info" className="learn-more-btn"> Learn More</Link>
        </div>
        </div>

        {/* Saved Scenarios Section moved to General Settings screen */}

        {errors.length > 0 && (
          <div className="error-messages">
            <h4>Please fix the following errors:</h4>
            <ul>
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <div style={{margin: '0 auto', maxWidth: '1400px', padding: 'var(--space-8)'}}>
          <div className="planner-container">
            <AdvancedRetirementPlanner
              initialData={currentScenario?.scenario_data}
              onSave={handleSaveScenario}
              saveName={saveName}
              isGuestMode={false}
              results={analysisResults}
              loading={isLoading}
              triggerAnalysis={triggerAnalysis}
              onAnalysisTriggered={handleAnalysisTriggered}
              savedScenarios={savedScenarios}
              onLoadScenario={handleLoadScenario}
              onDeleteScenario={handleDeleteScenario}
            />
          </div>
        </div>
      </div>
      
      <GuidanceModal
        isOpen={showGuidanceModal}
        onClose={() => setShowGuidanceModal(false)}
        onAccept={() => setShowGuidanceModal(false)}
        title={guidanceTitle}
        content={guidanceContent}
      />
    </div>
  );
};

export default RRSPCalculator; 